/* Timbrica — /jet-lag: интерфейс.
 *
 * Вся наука в engine.js. Здесь — выбор городов, форма и вывод расписания.
 *
 * ⚠️ ГЛАВНОЕ, ЧТО ОБЯЗАН СДЕЛАТЬ ИНТЕРФЕЙС: не дать перепутать пояс, в котором
 * читают расписание. До вылета строки идут по ДОМАШНЕМУ времени, после — по
 * МЕСТНОМУ. Тот же список часов, прочитанный не в том поясе, отправит человека
 * на свет ровно тогда, когда света надо избегать, — то есть сделает хуже, чем
 * если бы он не делал ничего. Поэтому пояс подписан на каждом дне, а не один
 * раз в шапке.
 */
import { buildPlan, tzOffsetHours } from './engine.js';

const CFG = window.JET_LAG_CFG || {};
const T = CFG.t || {};
const LOCALE = CFG.locale || 'en';

const tr = (k) => (T[k] != null ? T[k] : k);
const $ = (id) => document.getElementById(id);
const ae = (ev, p) => { try { window.__ae && window.__ae(ev, p || {}); } catch (e) {} };
const stage = (n) => { try { window.__stage && window.__stage(n); } catch (e) {} };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g,
  (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

let airportsIndex = null;
const state = { from: null, to: null };

// ── Аэропорты: общий датасет, он же несёт часовые пояса ─────────────────────
async function airports() {
  if (airportsIndex) return airportsIndex;
  const r = await fetch(CFG.airportsUrl, { credentials: 'omit' });
  const j = await r.json();
  const rows = j.rows.map((row) => ({
    iata: row[0], name: row[2], city: row[3], cc: j.cc[row[4]],
    lat: row[5], lon: row[6], tz: row[7] >= 0 ? j.tz[row[7]] : null,
    sched: row[8] === 1, rank: row[9],
  }));
  airportsIndex = rows;
  return rows;
}

function fold(s) {
  return (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function search(q, limit) {
  if (!airportsIndex) return [];
  const needle = fold(q).trim();
  if (needle.length < 2) return [];
  const out = [];
  for (const a of airportsIndex) {
    if (!a.tz) continue;                       // без пояса тул бесполезен
    let score = 0;
    const iata = a.iata.toLowerCase();
    if (iata === needle) score = 1000;
    else {
      const city = fold(a.city), name = fold(a.name);
      if (city.startsWith(needle)) score = 600;
      else if (name.startsWith(needle)) score = 500;
      else if (city.includes(needle)) score = 300;
      else if (name.includes(needle)) score = 200;
    }
    if (!score) continue;
    out.push([score + a.rank * 12 + (a.sched ? 30 : 0), a]);
  }
  out.sort((x, y) => y[0] - x[0]);
  return out.slice(0, limit || 8).map((x) => x[1]);
}

function label(a) {
  const city = (a.city || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  const name = a.name.replace(/\s+(International\s+)?Airport$/i, '');
  return (city && city !== name ? city : name) + ' (' + a.iata + ')';
}

function mountPicker(inputId, listId, key) {
  const input = $(inputId);
  const list = $(listId);
  if (!input || !list) return;
  let items = [];
  let active = -1;

  const close = () => { list.hidden = true; active = -1; };
  const render = () => {
    list.innerHTML = '';
    items.forEach((a, i) => {
      const li = document.createElement('li');
      li.className = 'jl-opt' + (i === active ? ' is-active' : '');
      li.setAttribute('role', 'option');
      li.innerHTML = '<span class="jl-opt-code">' + a.iata + '</span>' +
        '<span class="jl-opt-main">' + esc(label(a)) + '</span>' +
        '<span class="jl-opt-tz">' + esc(a.tz) + '</span>';
      li.addEventListener('mousedown', (e) => { e.preventDefault(); pick(a); });
      list.appendChild(li);
    });
    list.hidden = items.length === 0;
  };
  const pick = (a) => {
    state[key] = a;
    input.value = label(a);
    close();
    ae('jl_pick', { side: key, iata: a.iata });
    recompute();
  };

  input.addEventListener('input', async () => {
    state[key] = null;
    try { await airports(); } catch (e) { window.__toast && window.__toast(tr('airports_failed'), 'error'); return; }
    items = search(input.value, 8);
    active = -1;
    render();
  });
  input.addEventListener('keydown', (e) => {
    if (list.hidden) return;
    if (e.key === 'ArrowDown') { active = Math.min(active + 1, items.length - 1); render(); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { active = Math.max(active - 1, 0); render(); e.preventDefault(); }
    else if (e.key === 'Enter' && active >= 0) { pick(items[active]); e.preventDefault(); }
    else if (e.key === 'Escape') close();
  });
  input.addEventListener('blur', () => setTimeout(close, 120));
  input.addEventListener('focus', () => { airports().catch(() => {}); }, { once: true });
}

// ── Расчёт и вывод ──────────────────────────────────────────────────────────
function hoursFromTimeInput(id, fallback) {
  const v = ($(id) || {}).value || '';
  const m = v.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return fallback;
  return Math.min(23.99, Math.max(0, +m[1] + +m[2] / 60));
}

function recompute() {
  const out = $('jl-result');
  if (!out) return;

  if (!state.from || !state.to) {
    out.innerHTML = '<p class="jl-hint">' + esc(tr('need_route')) + '</p>';
    return;
  }

  const dateStr = ($('jl-date') || {}).value || '';
  const when = dateStr ? new Date(dateStr + 'T12:00:00Z') : new Date();
  const offFrom = tzOffsetHours(state.from.tz, when);
  const offTo = tzOffsetHours(state.to.tz, when);
  if (offFrom === null || offTo === null) {
    out.innerHTML = '<p class="jl-hint">' + esc(tr('tz_unknown')) + '</p>';
    return;
  }

  const plan = buildPlan({
    diffHours: offTo - offFrom,
    usualWake: hoursFromTimeInput('jl-wake', 7),
    usualSleep: hoursFromTimeInput('jl-sleep', 23),
    daysBefore: parseInt(($('jl-prep') || {}).value || '0', 10),
    melatonin: !!($('jl-melatonin') || {}).checked,
    caffeine: !!($('jl-caffeine') || {}).checked,
    flightDate: when,
  });

  render(plan, offTo - offFrom);
  ae('jl_plan', { hours: plan.totalHours, dir: plan.direction, long: plan.viaLongWay });
  stage('plan');
}

function render(plan, rawDiff) {
  const out = $('jl-result');
  const parts = [];

  if (plan.direction === 'none') {
    parts.push('<div class="jl-verdict"><div class="jl-big">' + esc(tr('no_shift')) + '</div>' +
      '<p>' + esc(tr('no_shift_note')) + '</p></div>');
    out.innerHTML = parts.join('');
    return;
  }

  const dirWord = plan.direction === 'advance' ? tr('dir_advance') : tr('dir_delay');
  parts.push(
    '<div class="jl-verdict">' +
    '<div class="jl-big">' + esc(tr('shift_by').replace(':h', String(plan.totalHours))) + '</div>' +
    '<div class="jl-dir">' + esc(dirWord) + ' · ' + esc(tr('days_needed').replace(':n', String(plan.daysNeeded))) + '</div>' +
    '</div>'
  );

  // ⚠️ Длинный путь надо ОБЪЯСНИТЬ, иначе он выглядит ошибкой: человек летит на
  // восток, а тул велит ложиться позже.
  if (plan.viaLongWay) {
    parts.push('<p class="jl-warn">' + esc(
      tr('long_way').replace(':raw', String(Math.abs(Math.round(rawDiff)))).replace(':h', String(plan.totalHours))
    ) + '</p>');
  }
  if (plan.notes.includes('slow_adaptation')) {
    parts.push('<p class="jl-note">' + esc(tr('slow_adaptation')) + '</p>');
  }
  if (plan.notes.includes('prep_capped')) {
    parts.push('<p class="jl-note">' + esc(tr('prep_capped')) + '</p>');
  }

  parts.push('<div class="jl-days">');
  for (const d of plan.days) {
    const title = d.beforeFlight
      ? tr('day_before').replace(':n', String(Math.abs(d.dayIndex)))
      : (d.dayIndex === 0 ? tr('day_flight') : tr('day_after').replace(':n', String(d.dayIndex)));

    parts.push(
      '<section class="jl-day' + (d.beforeFlight ? ' is-before' : '') + '">' +
      '<header class="jl-day-head"><h3>' + esc(title) + '</h3>' +
      '<span class="jl-clock">' + esc(d.clock === 'home' ? tr('clock_home') : tr('clock_dest')) + '</span></header>' +
      '<ul class="jl-items">' +
      item('sun', tr('seek_light'), d.seekLight.from + ' — ' + d.seekLight.to, tr('seek_light_note')) +
      item('moon', tr('avoid_light'), d.avoidLight.from + ' — ' + d.avoidLight.to, tr('avoid_light_note')) +
      item('bed', tr('sleep'), d.sleep.from + ' — ' + d.sleep.to, '') +
      (d.melatonin ? item('pill', tr('melatonin'), d.melatonin, tr('melatonin_note')) : '') +
      (d.caffeineUntil ? item('cup', tr('caffeine'), tr('caffeine_until').replace(':t', d.caffeineUntil), tr('caffeine_note')) : '') +
      '</ul></section>'
    );
  }
  parts.push('</div>');

  parts.push('<p class="jl-note">' + esc(tr('cbt_estimate')) + '</p>');

  out.innerHTML = parts.join('');
}

function item(icon, label, value, note) {
  return '<li class="jl-item jl-' + icon + '"><span class="jl-item-label">' + esc(label) + '</span>' +
    '<span class="jl-item-value">' + esc(value) + '</span>' +
    (note ? '<span class="jl-item-note">' + esc(note) + '</span>' : '') + '</li>';
}

// ── Старт ───────────────────────────────────────────────────────────────────
function init() {
  mountPicker('jl-from', 'jl-from-list', 'from');
  mountPicker('jl-to', 'jl-to-list', 'to');
  ['jl-date', 'jl-wake', 'jl-sleep', 'jl-prep', 'jl-melatonin', 'jl-caffeine'].forEach((id) => {
    const el = $(id);
    if (el) el.addEventListener('change', recompute);
    if (el && el.tagName === 'INPUT') el.addEventListener('input', recompute);
  });

  if (window.__toolSettings) {
    window.__toolSettings.bind('jet-lag', $('jl-wake'), 'wake', '07:00');
    window.__toolSettings.bind('jet-lag', $('jl-sleep'), 'sleep', '23:00');
  }

  recompute();
  stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
